import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

export const createStudent = async (payload) => {
  const response = await fetch(`${API_URL}/api/student`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    // The server explains exactly what it rejected — which fields, or which
    // value is duplicated. Throwing a flat "Failed to create student" threw
    // that away and left the admin staring at a form with nothing marked.
    let detail = null;
    try { detail = await response.json(); } catch (_) { /* non-JSON error body */ }

    const err = new Error(
      (detail && (detail.message || detail.error)) ||
      `Failed to create student (HTTP ${response.status})`
    );
    // Per-field messages, so the form can highlight the offending inputs.
    err.fields = (detail && detail.fields) || null;
    err.status = response.status;
    throw err;
  }

  return await response.json();
};

export const fetchStudents = async (page, pageSize) => {
  try {
    // The API reads `pageSize`, not `limit`.
    const res = await fetch(`${API_URL}/api/student?page=${page || 1}&pageSize=${pageSize || 500}`);
    const response = await res.json();

    return {
      data: response.data,
      pagination: response.pagination,
    };
  } catch (error) {
    throw error;
  }
};

export const fetchStudentById = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/api/student/${id}`);
    return response.data.data;
  } catch (error) {
    throw error;
  }
};

const buildEmailHtml = (title, body) => {
  const htmlBody = body.replace(/\n/g, "<br>");
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin:0; padding:0; background-color:#f4f4f4; font-family: Arial, sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f4f4f4; padding: 40px 0;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color:#ffffff; border-radius:8px; overflow:hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.08);">
          <tr>
            <td style="background-color:#0A0A0A; padding: 24px 40px; text-align:center;">
              <img src="https://uraansoftskills.com/SkillVerse/images/Skillverse-Circle.png" alt="MySkillverse" height="40" style="display:block; margin:0 auto;">
            </td>
          </tr>
          <tr>
            <td style="padding: 40px;">
              <h1 style="margin:0 0 16px; font-size:22px; color:#0A0A0A;">${title}</h1>
              <div style="font-size:15px; color:#444444; line-height:1.7;">
                ${htmlBody}
              </div>
            </td>
          </tr>
          <tr>
            <td style="padding: 0 40px;">
              <hr style="border:none; border-top:1px solid #eeeeee;">
            </td>
          </tr>
          <tr>
            <td style="padding: 24px 40px; text-align:center;">
              <p style="margin:0; font-size:12px; color:#999999;">
                © 2026 MySkillverse · myskillverse.ai<br>
                <a href="https://myskillverse.ai" style="color:#999999;">Visit Website</a>
              </p>
            </td>
          </tr> 
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
};

export const fetchWhatsAppTemplates = async () => {
  const response = await fetch(`${API_URL}/api/whatsapp-templates`);
  if (!response.ok) throw new Error("Failed to fetch WhatsApp templates");
  const { data } = await response.json();
  return data;
};

// whatsappConfig: { templateName: string, paramSelections: Array<{ type: string, value?: string }> }
export const sendStudentNotification = async (studentId, type, title, body, attachments, whatsappConfig) => {
  const formData = new FormData();
  formData.append("type", type);
  if (type === "email") {
    formData.append("title", title);
    formData.append("body", buildEmailHtml(title, body));
    attachments.forEach((file) => formData.append("attachments", file));
  } else {
    formData.append("templateName", whatsappConfig.templateName);
    formData.append("paramSelections", JSON.stringify(whatsappConfig.paramSelections));
  }

  const response = await fetch(`${API_URL}/api/student/${studentId}/notify`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to send notification");
  }

  return await response.json();
};

export const sendBatchNotification = async (batchId, type, title, body, attachments, whatsappConfig, emailConfig) => {
  const formData = new FormData();
  formData.append("type", type);
  if (type === "email") {
    formData.append("title", title);
    if (emailConfig?.paramSelections && Object.keys(emailConfig.paramSelections).length > 0) {
      formData.append("emailBodyTemplate", body);
      formData.append("emailParamSelections", JSON.stringify(emailConfig.paramSelections));
    } else {
      formData.append("body", buildEmailHtml(title, body));
    }
    attachments.forEach((file) => formData.append("attachments", file));
  } else {
    formData.append("templateName", whatsappConfig.templateName);
    formData.append("paramSelections", JSON.stringify(whatsappConfig.paramSelections));
  }

  const response = await fetch(`${API_URL}/api/batches/${batchId}/notify`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to send batch notification");
  }

  return await response.json();
};
